import { useTranslation } from 'react-i18next'

import {
  Alert,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import {
  type BulkWithdrawOutcome,
  offersReload,
} from '~/pages/SpotBidding/models/bulkWithdrawResult'

type Props = {
  outcome: BulkWithdrawOutcome | undefined
  onDismiss: () => void
  /** 「再読み込み」: 通常入札とブロック入札の両方の一覧を読み直す。 */
  onReload: () => void
}

/**
 * 入札中の札の一括取下 (AC-BSP-116) の確定の時点の結果。 受け付けなかった札・結果を確認できなかった札が
 * ある時だけ出す。 全件を受け付けた時は何も出さない (受け付けた札の成立・取下失敗は後で分かる)。
 *
 * 通常入札タブ・ブロック入札タブのどちらからでも実行でき、 結果は通常入札 + ブロック入札の合算の
 * 画面全体の結果なので、 呼び出し元はタブの外 (タブバーより上のステータス帯) に 1 つだけ置く。
 * 閉じる操作で消す。
 *
 * - 受け付けなかった札: 札ごとの文言は出さず、 1 つのエラーバナーに理由ごとの文を並べる (入札締切後・
 *   状態が進んでいた・内容だけ先に更新されていた)。 内容だけ先に更新されていた札がある時だけ
 *   「再読み込み」 を置き、 1 回で両方の一覧を読み直す
 * - RPC 自体が失敗した分: 通信の失敗では backend が commit 済みでも応答が届かないことがあるので、
 *   取り下げた / 取り下げなかったと言わず、 一覧を読み直したことと確認を促す。 再操作を促す理由文は、
 *   業務上の拒否の時だけ添える (結果が分からない時に再操作を促すと二重に送るおそれがある)
 */
export const BulkWithdrawResultBanner = ({
  outcome,
  onDismiss,
  onReload,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  if (outcome === undefined) return null
  const { refusals, unconfirmed, acceptedCount } = outcome
  const unconfirmedCount = unconfirmed.reduce((sum, u) => sum + u.count, 0)
  const refusalSentences =
    refusals === undefined
      ? []
      : [
          refusals.deadlinePassed
            ? t('page:biddingSpot.bulkWithdraw.result.deadlinePassed')
            : undefined,
          refusals.advanced.normal + refusals.advanced.block > 0
            ? t('page:biddingSpot.bulkWithdraw.result.advanced', {
                normal: refusals.advanced.normal,
                block: refusals.advanced.block,
              })
            : undefined,
          refusals.changed.normal + refusals.changed.block > 0
            ? t('page:biddingSpot.bulkWithdraw.result.changed', {
                normal: refusals.changed.normal,
                block: refusals.changed.block,
              })
            : undefined,
        ].filter((s): s is string => s !== undefined)

  return (
    // `flexShrink={0}`: 呼び出し元は高さ拘束された column flex で、 既定だと押し潰されて
    // DS `Alert` の overflow で中身が切れる (`BulkApprovePanel` と同じ)。
    <VStack align="stretch" flexShrink={0} gap={2}>
      {refusalSentences.length > 0 && (
        // 1 文で済む時は見出しだけにする (`ui-message-guide.md`「バナー」)。 理由が複数の時は理由ごとの文を
        // 本文に並べる (見出しの有無は Des 確認 #12384 で決める)。
        <Alert
          description={
            refusalSentences.length > 1 ? (
              <VStack align="stretch" gap={1}>
                {refusalSentences.map((sentence) => (
                  <Text key={sentence}>{sentence}</Text>
                ))}
              </VStack>
            ) : undefined
          }
          // 操作の後に非同期に現れる知らせなので、 支援技術に読ませる (DS の Alert は role を出さない)。
          role="alert"
          status="error"
          title={
            refusalSentences.length === 1 ? refusalSentences[0] : undefined
          }
        >
          {offersReload(outcome) && (
            <AlertActions>
              <TertiaryButton onClick={onReload} size="sm">
                {t('page:biddingSpot.bulkWithdraw.result.reload')}
              </TertiaryButton>
            </AlertActions>
          )}
          <AlertDismissButton onClick={onDismiss} />
        </Alert>
      )}
      {unconfirmed.length > 0 && (
        <Alert
          description={
            <VStack align="stretch" gap={1}>
              {unconfirmed.map(({ message }) =>
                message === undefined ? null : (
                  <Text key={message}>{message}</Text>
                ),
              )}
              <Text>
                {t('page:biddingSpot.bulkWithdraw.result.unconfirmedBody')}
              </Text>
              {acceptedCount > 0 && (
                <Text>
                  {t('page:biddingSpot.bulkWithdraw.result.acceptedNote', {
                    accepted: acceptedCount,
                  })}
                </Text>
              )}
            </VStack>
          }
          role="alert"
          status="error"
          title={t('page:biddingSpot.bulkWithdraw.result.unconfirmedTitle', {
            unconfirmed: unconfirmedCount,
          })}
        >
          <AlertDismissButton onClick={onDismiss} />
        </Alert>
      )}
    </VStack>
  )
}
